import { Box, Icon, Input, Popover, Text, useFilter } from '@chakra-ui/react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentProps } from 'react'

import {
  getStateProvinceFlatOptions,
  getStateProvinceSections,
  type StateOrProvinceOption,
  type StateProvinceVariant,
} from '../constants/stateProvinceOptions'

const DEFAULT_SECTION_LABELS = {
  unitedStates: 'United States',
  usTerritories: 'U.S. territories',
  canada: 'Canada',
} as const

const DEFAULT_EMPTY_OPTION: StateOrProvinceOption = {
  value: '',
  label: '----------',
}

export interface StateProvinceComboboxProps {
  /**
   * `phoneNumber` — matches `PhoneNumber.StateOrProvince` (E911 / phone APIs; 65 codes; no AS/GU/MP/VI).
   * `company` — matches `Company.StateOrProvince` (69 codes; includes U.S. territories after the US block).
   */
  variant: StateProvinceVariant
  value: string
  onValueChange: (value: string) => void
  /** When true, first row is unselected (empty API value); label defaults to `----------`. */
  allowEmpty?: boolean
  emptyOptionLabel?: string
  placeholder?: string
  emptySearchMessage?: string
  /** Section headings in the dropdown (English defaults). */
  sectionLabels?: {
    unitedStates?: string
    usTerritories?: string
    canada?: string
  }
  id?: string
  size?: 'sm' | 'md'
  maxH?: string
  /** Clears the search query whenever this value changes (e.g. `open` from a dialog). */
  resetSearchKey?: unknown
  inputProps?: Omit<
    ComponentProps<typeof Input>,
    'id' | 'placeholder' | 'value' | 'onChange'
  >
}

export const StateProvinceCombobox = memo<StateProvinceComboboxProps>(
  ({
    variant,
    value,
    onValueChange,
    allowEmpty = false,
    emptyOptionLabel,
    placeholder = 'Search...',
    emptySearchMessage = 'No matches',
    sectionLabels,
    id,
    size = 'md',
    maxH = '280px',
    resetSearchKey,
    inputProps,
  }) => {
    const { contains } = useFilter({ sensitivity: 'base' })

    const noneOption: StateOrProvinceOption = useMemo(
      () =>
        emptyOptionLabel
          ? { value: '', label: emptyOptionLabel }
          : DEFAULT_EMPTY_OPTION,
      [emptyOptionLabel],
    )

    const mergedSectionLabels = useMemo(
      () => ({
        unitedStates:
          sectionLabels?.unitedStates ?? DEFAULT_SECTION_LABELS.unitedStates,
        usTerritories:
          sectionLabels?.usTerritories ?? DEFAULT_SECTION_LABELS.usTerritories,
        canada: sectionLabels?.canada ?? DEFAULT_SECTION_LABELS.canada,
      }),
      [
        sectionLabels?.unitedStates,
        sectionLabels?.usTerritories,
        sectionLabels?.canada,
      ],
    )

    const sections = useMemo(
      () => getStateProvinceSections(variant, mergedSectionLabels),
      [variant, mergedSectionLabels],
    )

    const flatOptions = useMemo(
      () => getStateProvinceFlatOptions(variant),
      [variant],
    )

    const allOptions = useMemo(
      () => (allowEmpty ? [noneOption, ...flatOptions] : flatOptions),
      [allowEmpty, noneOption, flatOptions],
    )

    const labelForValue = useCallback(
      (v: string) => {
        if (v === '') return ''
        return flatOptions.find((o) => o.value === v)?.label ?? ''
      },
      [flatOptions],
    )

    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)

    const selectedLabel = labelForValue(value)

    // The text shown in the input: when open, show the query; when closed, show the selected label.
    const displayValue = open ? query : selectedLabel

    const filteredOptions = useMemo(() => {
      if (!query.trim()) return null
      return allOptions.filter(
        (o) => contains(o.label, query) || contains(o.value, query),
      )
    }, [query, allOptions, contains])

    const isFiltered = filteredOptions !== null
    const visibleItems = isFiltered ? filteredOptions : null

    useEffect(() => {
      setQuery('')
    }, [resetSearchKey, variant])

    // When opening, clear the query so the user can start typing fresh.
    // When closing, reset.
    const handleOpen = useCallback(() => {
      setQuery(selectedLabel)
      setHighlightedIndex(-1)
      setOpen(true)
    }, [selectedLabel])

    const handleClose = useCallback(() => {
      setOpen(false)
      setQuery('')
      setHighlightedIndex(-1)
    }, [])

    const handleSelect = useCallback(
      (optionValue: string) => {
        onValueChange(optionValue)
        handleClose()
      },
      [onValueChange, handleClose],
    )

    const handleClear = useCallback(() => {
      onValueChange('')
      setQuery('')
    }, [onValueChange])

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        setQuery(val)
        setHighlightedIndex(val.trim() ? 0 : -1)
        if (!open) setOpen(true)
      },
      [open],
    )

    // Keyboard navigation
    const getVisibleList = useCallback((): StateOrProvinceOption[] => {
      if (visibleItems) return visibleItems
      return allOptions
    }, [visibleItems, allOptions])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        const items = getVisibleList()
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          if (!open) {
            handleOpen()
            return
          }
          setHighlightedIndex((prev) =>
            prev < items.length - 1 ? prev + 1 : 0,
          )
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          if (!open) {
            handleOpen()
            return
          }
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : items.length - 1,
          )
        } else if (e.key === 'Enter') {
          e.preventDefault()
          if (
            open &&
            highlightedIndex >= 0 &&
            highlightedIndex < items.length
          ) {
            handleSelect(items[highlightedIndex].value)
          }
        } else if (e.key === 'Escape') {
          handleClose()
        }
      },
      [
        open,
        highlightedIndex,
        getVisibleList,
        handleOpen,
        handleClose,
        handleSelect,
      ],
    )

    // Scroll highlighted item into view
    useEffect(() => {
      if (highlightedIndex < 0 || !listRef.current) return
      const el = listRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`,
      )
      el?.scrollIntoView({ block: 'nearest' })
    }, [highlightedIndex])

    const isSm = size === 'sm'
    const fontSize = isSm ? 'xs' : 'sm'

    const renderItem = (item: StateOrProvinceOption, index: number) => {
      const isSelected = item.value === value
      const isHighlighted = index === highlightedIndex
      return (
        <Box
          key={item.value || 'empty'}
          data-index={index}
          px={3}
          py={1.5}
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          fontSize={fontSize}
          bg={isHighlighted ? 'bg.muted' : undefined}
          _hover={{ bg: 'bg.muted' }}
          onMouseEnter={() => setHighlightedIndex(index)}
          onMouseDown={(e) => {
            e.preventDefault()
            handleSelect(item.value)
          }}
        >
          <Text>{item.label}</Text>
          {isSelected && <Icon as={Check} boxSize={3.5} color="fg.default" />}
        </Box>
      )
    }

    let flatIndex = 0

    return (
      <Popover.Root
        open={open}
        onOpenChange={(details) => {
          if (details.open) handleOpen()
          else handleClose()
        }}
        positioning={{ strategy: 'fixed', sameWidth: true }}
        autoFocus={false}
      >
        <Popover.Trigger asChild>
          <Box position="relative" width="100%">
            <Input
              ref={inputRef}
              id={id}
              placeholder={placeholder}
              value={displayValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onClick={() => {
                if (!open) handleOpen()
              }}
              bg="bg.subtle"
              borderColor="border.default"
              fontSize={fontSize}
              size={size}
              autoComplete="off"
              pr={value ? '60px' : '36px'}
              {...inputProps}
            />
            <Box
              position="absolute"
              right={1}
              top="50%"
              transform="translateY(-50%)"
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              {value && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClear()
                  }}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px',
                    background: 'none',
                    border: 'none',
                  }}
                >
                  <X size={isSm ? 14 : 16} />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (open) handleClose()
                  else handleOpen()
                  inputRef.current?.focus()
                }}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px',
                  background: 'none',
                  border: 'none',
                }}
              >
                <ChevronsUpDown size={isSm ? 16 : 18} />
              </button>
            </Box>
          </Box>
        </Popover.Trigger>
        <Popover.Positioner>
          <Popover.Content
            ref={listRef}
            maxH={maxH}
            overflowY="auto"
            p={1}
            shadow="md"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            bg="bg.panel"
          >
            {isFiltered && visibleItems ? (
              visibleItems.length === 0 ? (
                <Box px={3} py={2}>
                  <Text fontSize={fontSize} color="fg.muted">
                    {emptySearchMessage}
                  </Text>
                </Box>
              ) : (
                visibleItems.map((item, i) => renderItem(item, i))
              )
            ) : (
              <>
                {sections.map((section, sectionIdx) => (
                  <Box key={section.id}>
                    {sectionIdx > 0 && (
                      <Box height="1px" bg="border.muted" mx={2} my={1.5} />
                    )}
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      color="fg.muted"
                      px={3}
                      pt={sectionIdx > 0 ? 1 : 1.5}
                      pb={1}
                      userSelect="none"
                    >
                      {section.label}
                    </Text>
                    {section.items.map((item) => renderItem(item, flatIndex++))}
                  </Box>
                ))}
              </>
            )}
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    )
  },
)

StateProvinceCombobox.displayName = 'StateProvinceCombobox'
